import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Table } from 'antd';
import { isEqual } from 'lodash';

import Pficon from 'components/Pficon';
import { DIRECTION } from 'containers/App/constants';
import MatrixAndConceptsViewOnlyTableHeader from './MatrixAndConceptsViewOnlyTableHeader';

function MatrixAndConceptsTable({
  tableId,
  tableType,
  inputType = 'textArea',
  tableRows,
  tableColumns,
  cellColor = 'force-background-grey-1',
  tableColumnWidth = 280,
  renderCellContent,
  tablefixedColumnWidth = 150,
  maxColumnChars,
  maxRowChars,
}) {
  const scrollHorizontally = ({
    scrollToEnd = false,
    direction = DIRECTION.RIGHT,
  }) => {
    const scrollContainer = document.querySelector(
      `#${tableId} .ant-table-content`,
    );
    const scrollElem = scrollContainer.querySelector('table');

    const containerRect = scrollContainer.getBoundingClientRect();
    const elemRect = scrollElem.getBoundingClientRect();
    let scrollLeft = elemRect.width - containerRect.width;

    if (!scrollToEnd) {
      const distance =
        (elemRect.width - tablefixedColumnWidth) / tableColumns.length;

      scrollLeft =
        containerRect.left -
        elemRect.left +
        (direction === DIRECTION.RIGHT ? distance : -distance);
    }

    scrollContainer.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  };

  const [tableColumnsCount, setTableColumnsCount] = useState(0);

  useEffect(() => {
    if (tableColumnsCount !== tableColumns.length) {
      setTableColumnsCount(tableColumns.length);
    }
  }, [tableColumns]);

  const { columnsData, dataSource } = useMemo(() => {
    const columns = [
      {
        width: tablefixedColumnWidth,
        dataIndex: 'item',
        key: 'item',
        fixed: 'left',
        onHeaderCell: () => ({
          className: classNames(
            'padding-none force-text-weight-regular text-size-secondary force-border-right-grey-4',
            'border-bottom-grey-4 padding-sm-important',
            cellColor,
          ),
        }),
        onCell: (record, rowIndex) => ({
          className: classNames('border-right-grey-4 padding-none', cellColor, {
            'force-border-bottom-grey-4': rowIndex !== tableRows.length - 1,
            'force-border-bottom-transparent':
              rowIndex === tableRows.length - 1,
          }),
        }),
        render: (row) => (
          <MatrixAndConceptsViewOnlyTableHeader
            tableType={tableType}
            headerType="row"
            inputType={inputType}
            item={row}
            maxLength={maxRowChars}
          />
        ),
      },
    ];

    tableColumns.forEach((column, columnIndex) => {
      columns.push({
        key: column.refId,
        dataIndex: `column-${column.refId}`,
        onHeaderCell: () => ({
          className: classNames(
            'force-text-weight-regular force-border-bottom-grey-4',
            'vertical-align-bottom padding-none',
            cellColor,
            {
              'force-border-right-grey-4':
                columnIndex !== tableColumns.length - 1,
              'force-border-right-none':
                columnIndex === tableColumns.length - 1,
            },
          ),
        }),
        onCell: (record, cellIndex) => ({
          className: classNames(
            'position-relative padding-sm-important',
            cellColor,
            {
              'force-border-right-grey-4':
                columnIndex !== tableColumns.length - 1,
              'force-border-bottom-grey-4': cellIndex !== tableRows.length - 1,
              'force-border-bottom-transparent':
                cellIndex === tableRows.length - 1,
            },
          ),
        }),
        width: tableColumnWidth,
        title: (
          <MatrixAndConceptsViewOnlyTableHeader
            key={column.refId}
            tableType={tableType}
            headerType="column"
            inputType={inputType}
            item={{
              ...column,
              index: columnIndex,
            }}
            maxLength={maxColumnChars}
          />
        ),
        render: renderCellContent,
      });
    });

    const data = tableRows.map((row, index) => {
      const dataObject = {
        key: row.refId,
        item: {
          ...row,
          index,
        },
      };

      if (row.values) {
        row.values.forEach((attributeValue, attributeValueIndex) => {
          dataObject[`column-${tableColumns[attributeValueIndex].refId}`] = {
            ...attributeValue,
            attributeRefId: row.refId,
            attributeName: row.name,
          };
        });
      }

      return dataObject;
    });

    return {
      columnsData: columns,
      dataSource: data,
    };
  }, [tableRows, tableColumns, cellColor]);

  return (
    <div className="position-relative">
      {tableColumns.length > 3 && (
        <div
          className="position-absolute"
          style={{ top: -24, right: 5, zIndex: 5 }}
        >
          <button
            className="cursor-pointer padding-none"
            type="button"
            onClick={() => scrollHorizontally({ direction: DIRECTION.LEFT })}
          >
            <Pficon type="keyboard_arrow_left" className="fs-16" />
          </button>
          <button
            className="cursor-pointer padding-none"
            type="button"
            onClick={() => scrollHorizontally({ direction: DIRECTION.RIGHT })}
          >
            <Pficon type="keyboard_arrow_right" className="fs-16" />
          </button>
        </div>
      )}
      <Table
        id={tableId}
        className={classNames(
          'table-node table-node--hide margin-btm-xs',
          'overflow-hidden background-grey-1 border-top-grey-4 border-bottom-grey-4',
        )}
        columns={columnsData}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: tableColumns.length * tableColumnWidth }}
      />
    </div>
  );
}

MatrixAndConceptsTable.propTypes = {
  tableId: PropTypes.string,
  tableType: PropTypes.string,
  inputType: PropTypes.string,
  tablefixedColumnWidth: PropTypes.number,
  tableColumnWidth: PropTypes.number,
  tableColumns: PropTypes.array,
  tableRows: PropTypes.array,
  maxColumnChars: PropTypes.number,
  maxRowChars: PropTypes.number,
  renderCellContent: PropTypes.func,
  cellColor: PropTypes.string,
};

export default React.memo(MatrixAndConceptsTable, isEqual);
